import React, { useState, useEffect } from 'react';
import classes from '../main.module.css'
import { db } from '../firebase';
import { doc, onSnapshot, collection, addDoc, query, where, getDocs } from 'firebase/firestore';

const CountdownTimer = () => {

    const [timeLeft, setTimeLeft] = useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
    });

    const [appDetails, setAppDetails] = useState({
        name: '',
        url: '',
        description: '',
        icon: ''
    });

    const [isLaunched, setIsLaunched] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [email, setEmail] = useState('');
    const [status, setStatus] = useState({ type: '', message: '' }); // type: 'loading', 'success', 'error'

    const validateEmail = (email) => {
        return String(email)
            .toLowerCase()
            .match(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            );
    };

    const handleSubscribe = async () => {
        if (!email) {
            setStatus({ type: 'error', message: 'Please enter an email address.' });
            return;
        }

        if (!validateEmail(email)) {
            setStatus({ type: 'error', message: 'Please enter a valid email address.' });
            return;
        }

        setStatus({ type: 'loading', message: 'Subscribing...' });

        try {
            // Check for potential duplicate
            const subscribersRef = collection(db, "subscribers");
            const q = query(subscribersRef, where("email", "==", email.toLowerCase()));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                setStatus({ type: 'error', message: 'This email is already subscribed!' });
                return;
            }

            // ADD to Firestore
            await addDoc(subscribersRef, {
                email: email.toLowerCase(),
                timestamp: new Date(),
                notified: false
            });

            setStatus({ type: 'success', message: 'Thank you! You will be notified when we launch.' });
            setEmail('');
        } catch (error) {
            console.error("Error subscribing:", error);
            setStatus({ type: 'error', message: 'Something went wrong. Please try again later.' });
        }
    };

    useEffect(() => {
        let intervalId;
        let targetDate;

        const calculateTimeLeft = (target) => {
            const difference = +target - +new Date();
            let timeLeft = { days: 0, hours: 0, minutes: 0, seconds: 0 };

            if (difference > 0) {
                timeLeft = {
                    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                    minutes: Math.floor((difference / 1000 / 60) % 60),
                    seconds: Math.floor((difference / 1000) % 60)
                };
                setIsLaunched(false);
            } else {
                setIsLaunched(true);
            }

            return timeLeft;
        };

        const docRef = doc(db, "appSettings", "launchConfig");

        // Use onSnapshot for real-time updates from Firestore
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                if (data.launchDate) {
                    targetDate = data.launchDate.toDate();

                    // Fetch app details
                    setAppDetails({
                        name: data.appName || 'Our New App',
                        url: data.appUrl || '#',
                        description: data.appDescription || 'Our latest application is now available!',
                        icon: data.appIcon || ''
                    });

                    // Initial calculation on data change
                    const initialTime = calculateTimeLeft(targetDate);
                    setTimeLeft(initialTime);
                    setIsLoading(false);

                    // Clear old interval if exists
                    if (intervalId) clearInterval(intervalId);

                    // Start or restart the countdown interval if not launched
                    if (+targetDate - +new Date() > 0) {
                        intervalId = setInterval(() => {
                            setTimeLeft(calculateTimeLeft(targetDate));
                        }, 1000);
                    }
                }
            } else {
                console.log("No launch config found in Firestore!");
                setIsLoading(false);
            }
        }, (error) => {
            console.error("Error listening to launch date from Firebase:", error);
            setIsLoading(false);
        });

        return () => {
            unsubscribe();
            if (intervalId) clearInterval(intervalId);
        };

    }, []);

    return (
        <section id="launch" className={classes.LaunchSection}>
            <div className={classes.Container}>
                <div className={classes.LaunchBannerWrapper}>
                    {/* Decorative Background Elements */}
                    <div className={classes.LaunchDecorativeBlob}></div>
                    <div className={classes.LaunchDecorativeBlobTwo}></div>

                    <div className={classes.LaunchContent}>
                        {isLoading ? (
                            <div className={classes.LaunchLoading}>Loading...</div>
                        ) : isLaunched ? (
                            <div className={classes.LaunchedCardModern}>
                                {appDetails.icon && <img src={appDetails.icon} alt="App Icon" className={classes.AppIcon} />}
                                <h2 className={classes.LaunchedTitle}>{appDetails.name} is Live!</h2>
                                <p className={classes.LaunchedDescription}>{appDetails.description}</p>
                                <a href={appDetails.url} target="_blank" rel="noopener noreferrer" className={classes.LaunchButtonAction}>
                                    View App Now
                                </a>
                            </div>
                        ) : (
                            <div className={classes.CountdownBannerGrid}>
                                <div className={classes.CountdownTextSide}>
                                    <span className={classes.ComingSoonBadge}>Coming Soon</span>
                                    <h2 className={classes.CountdownTitleModern}>Prepare for the next big thing.</h2>
                                    <p className={classes.CountdownSubtitleModern}>Our brand new application is launching very soon. Subscribe to get notified and receive exclusive early access benefits.</p>

                                    <div className={classes.LaunchSubscribeForm}>
                                        <input 
                                            type="email" 
                                            placeholder="Enter your email address" 
                                            className={classes.LaunchInput} 
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            onFocus={() => setStatus({ type: '', message: '' })}
                                        />
                                        <button 
                                            className={classes.LaunchNotifyBtn} 
                                            onClick={handleSubscribe}
                                            disabled={status.type === 'loading'}
                                        >
                                            {status.type === 'loading' ? 'Saving...' : 'Notify Me'}
                                        </button>
                                    </div>
                                    {status.message && (
                                        <p className={`${classes.LaunchStatus} ${classes[status.type]}`}>
                                            {status.message}
                                        </p>
                                    )}
                                </div>

                                <div className={classes.CountdownTimerSide}>
                                    <div className={classes.CountdownGlassCard}>
                                        <div className={classes.CountdownTimeUnit}>
                                            <span className={classes.CountdownTimeNumber}>{timeLeft.days}</span>
                                            <span className={classes.CountdownTimeLabel}>Days</span>
                                        </div>
                                        <div className={classes.CountdownDivider}>:</div>
                                        <div className={classes.CountdownTimeUnit}>
                                            <span className={classes.CountdownTimeNumber}>{String(timeLeft.hours).padStart(2, '0')}</span>
                                            <span className={classes.CountdownTimeLabel}>Hours</span>
                                        </div>
                                        <div className={classes.CountdownDivider}>:</div>
                                        <div className={classes.CountdownTimeUnit}>
                                            <span className={classes.CountdownTimeNumber}>{String(timeLeft.minutes).padStart(2, '0')}</span>
                                            <span className={classes.CountdownTimeLabel}>Mins</span>
                                        </div>
                                        <div className={classes.CountdownDivider}>:</div>
                                        <div className={classes.CountdownTimeUnit}>
                                            <span className={classes.CountdownTimeNumber}>{String(timeLeft.seconds).padStart(2, '0')}</span>
                                            <span className={classes.CountdownTimeLabel}>Secs</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    )

}

export default CountdownTimer;